import React, { useState } from 'react';
import { Box, Typography, Button, TextField } from '@mui/material';
import { colors, font, type as ty, layout } from '../theme/tokens';
import { FLEET, READY_UNIT_IDS, reportingDate, reportingTime } from '../data/fleet';
import { useFleet } from '../FleetContext';
import { useI18n } from '../i18n';

// ─────────────────────────────────────────────────────────────────────────────
// Fleet side menu: reporting date at the top, then the unit list.
//
// Units without demo data are listed but disabled — the fleet is 100 strong,
// and showing that scale matters more than hiding the gaps.
// ─────────────────────────────────────────────────────────────────────────────

const UnitRow: React.FC<{
  id: string;
  depot: string;
  note?: string;
  disabled: boolean;
  active: boolean;
  noDataLabel: string;
  onSelect: () => void;
}> = ({ id, depot, note, disabled, active, noDataLabel, onSelect }) => (
  <Button
    onClick={onSelect}
    disabled={disabled}
    disableRipple
    sx={{
      width: '100%',
      justifyContent: 'flex-start',
      textAlign: 'left',
      textTransform: 'none',
      px: 1.25,
      py: 0.85,
      minWidth: 0,
      borderRadius: layout.radius,
      borderLeft: `3px solid ${active ? colors.accent : 'transparent'}`,
      backgroundColor: active ? colors.accentWash : 'transparent',
      color: active ? colors.ink : colors.inkMuted,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 0,
      '&:hover': {
        backgroundColor: active ? colors.accentWash : colors.bgAlt,
        color: colors.ink,
      },
      '&.Mui-disabled': {
        color: colors.inkGhost,
        borderLeftColor: 'transparent',
      },
    }}
  >
    <Box component="span" sx={{
      width: '100%',
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: 1,
    }}>
      <Box component="span" sx={{
        fontFamily: font.mono,
        fontSize: '12px',
        fontWeight: active ? 700 : 500,
      }}>
        {id}
      </Box>
      <Box component="span" sx={{
        fontFamily: font.sans,
        fontSize: '11px',
        opacity: 0.7,
      }}>
        {disabled ? noDataLabel : depot}
      </Box>
    </Box>
    {note && (
      <Box component="span" sx={{
        fontFamily: font.sans,
        fontSize: '10.5px',
        lineHeight: 1.35,
        color: active ? colors.inkMuted : colors.inkFaint,
        mt: '2px',
      }}>
        {note}
      </Box>
    )}
  </Button>
);

const FleetSidebar: React.FC = () => {
  const { unitId, setUnitId } = useFleet();
  const { t, locale } = useI18n();
  const [filter, setFilter] = useState('');

  const shown = filter.trim()
    ? FLEET.filter(u => u.id.toLowerCase().includes(filter.trim().toLowerCase()))
    : FLEET;

  // Units with data float to the top: they are the only ones worth clicking,
  // and hunting for DX-09 in a 100-row list mid-demo is exactly the friction
  // a booth doesn't need.
  const ready = shown.filter(u => u.status === 'ready');
  const rest = shown.filter(u => u.status !== 'ready');

  const GroupLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography sx={{
      ...ty.eyebrow,
      fontSize: '9px',
      color: colors.inkFaint,
      px: 1.25,
      pt: 1,
      pb: 0.75,
    }}>
      {children}
    </Typography>
  );

  return (
    <Box
      component="aside"
      sx={{
        width: { xs: '100%', md: 252 },
        flexShrink: 0,
        alignSelf: 'flex-start',
        position: { md: 'sticky' },
        top: { md: `calc(${layout.topbarHeight} + 60px)` },
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: layout.radiusLg,
        overflow: 'hidden',
      }}
    >
      {/* Reporting date */}
      <Box sx={{
        px: 2,
        py: 1.75,
        backgroundColor: colors.bgAlt,
        borderBottom: `1px solid ${colors.border}`,
      }}>
        <Typography sx={{ ...ty.eyebrow, fontSize: '9px', color: colors.inkFaint, mb: 0.5 }}>
          {t('fleet.reportingDate')}
        </Typography>
        <Typography sx={{
          fontFamily: font.sans,
          fontSize: '14px',
          fontWeight: 700,
          letterSpacing: '-0.01em',
          color: colors.ink,
        }}>
          {reportingDate(locale)}
        </Typography>
        <Typography sx={{ ...ty.meta, fontSize: '10px', color: colors.inkFaint, mt: 0.25 }}>
          {t('fleet.asOf')} {reportingTime(locale)} · {t('fleet.local')}
        </Typography>
      </Box>

      {/* Header */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${colors.border}` }}>
        <Typography sx={{ ...ty.eyebrow, fontSize: '9px', color: colors.inkFaint }}>
          {t('fleet.title')}
        </Typography>
        <Typography sx={{ fontFamily: font.sans, fontSize: '13px', fontWeight: 500, color: colors.ink }}>
          {READY_UNIT_IDS.length}
          <Box component="span" sx={{ color: colors.inkFaint, fontWeight: 400 }}>
            {' '}{t('fleet.withData', { total: FLEET.length })}
          </Box>
        </Typography>
      </Box>

      {/* Filter */}
      <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={t('fleet.filter')}
          value={filter}
          onChange={e => setFilter(e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': { fontFamily: font.mono, fontSize: '12px' },
            '& .MuiOutlinedInput-input': { py: '8px' },
          }}
        />
      </Box>

      {/* List */}
      <Box sx={{
        maxHeight: { xs: 300, md: 400 },
        overflowY: 'auto',
        px: 1,
        pb: 1,
        WebkitOverflowScrolling: 'touch',
      }}>
        {shown.length === 0 ? (
          <Typography sx={{
            fontFamily: font.sans, fontSize: '12px',
            color: colors.inkFaint, px: 1.25, py: 2,
          }}>
            {t('fleet.noMatch', { q: filter })}
          </Typography>
        ) : (
          <>
            {ready.length > 0 && (
              <>
                <GroupLabel>{t('fleet.ready')}</GroupLabel>
                {ready.map(u => (
                  <UnitRow
                    key={u.id}
                    id={u.id}
                    depot={u.depot}
                    note={u.note}
                    disabled={false}
                    active={u.id === unitId}
                    noDataLabel={t('fleet.noData')}
                    onSelect={() => setUnitId(u.id)}
                  />
                ))}
              </>
            )}

            {rest.length > 0 && (
              <>
                <Box sx={{
                  mt: ready.length > 0 ? 1 : 0,
                  pt: ready.length > 0 ? 0.5 : 0,
                  borderTop: ready.length > 0 ? `1px solid ${colors.border}` : 'none',
                }}>
                  <GroupLabel>{t('fleet.remaining')}</GroupLabel>
                </Box>
                {rest.map(u => (
                  <UnitRow
                    key={u.id}
                    id={u.id}
                    depot={u.depot}
                    disabled
                    active={false}
                    noDataLabel={t('fleet.noData')}
                    onSelect={() => setUnitId(u.id)}
                  />
                ))}
              </>
            )}
          </>
        )}
      </Box>

      <Box sx={{
        px: 2,
        py: 1.5,
        borderTop: `1px solid ${colors.border}`,
        backgroundColor: colors.bgAlt,
      }}>
        <Typography sx={{ fontFamily: font.sans, fontSize: '11px', color: colors.inkFaint, lineHeight: 1.5 }}>
          {t('fleet.footnote', { units: READY_UNIT_IDS.join(', ') })}
        </Typography>
      </Box>
    </Box>
  );
};

export default FleetSidebar;
